import type { NearbyCategoryConfig, NearbyCategoryKey } from './nearby.types';
import { fetchNearbyHotels } from './adapters/hotels';
import { fetchNearbySports } from './adapters/sports';
import { fetchNearbyShows } from './adapters/shows';
import { fetchNearbyRestaurants } from './adapters/restaurants';
import { fetchNearbyConcerts } from './adapters/concerts';
import { NearbyHotelCard } from '../../_components/nearby/cards/NearbyHotelCard';
import { NearbySportCard } from '../../_components/nearby/cards/NearbySportCard';
import { NearbyShowCard } from '../../_components/nearby/cards/NearbyShowCard';
import { NearbyRestaurantCard } from '../../_components/nearby/cards/NearbyRestaurantCard';
import { NearbyConcertCard } from '../../_components/nearby/cards/NearbyConcertCard';

// Heterogeneous registry: each entry's `fetchData`/`renderCard` pair is
// individually type-safe where it's written below; the shared Record
// type necessarily erases that per-key precision since every category's
// item shape differs.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const NEARBY_CATEGORIES: Record<NearbyCategoryKey, NearbyCategoryConfig<any>> = {
  hotels: {
    buildLabel: (name) => (name ? `Hotels Near ${name}` : 'Nearby Hotels'),
    fetchData: fetchNearbyHotels,
    renderCard: (item, key, ctx) => <NearbyHotelCard key={key} hotel={item} referenceName={ctx.referenceName} />,
  },
  sports: {
    buildLabel: (name) => (name ? `Sports Events Near ${name}` : 'Nearby Sports'),
    fetchData: fetchNearbySports,
    renderCard: (item, key, ctx) => <NearbySportCard key={key} event={item} referenceName={ctx.referenceName} />,
  },
  shows: {
    buildLabel: (name) => (name ? `Shows Near ${name}` : 'Nearby Shows'),
    fetchData: fetchNearbyShows,
    renderCard: (item, key, ctx) => (
      <NearbyShowCard key={key} show={item} bookingDate={ctx.dateFrom} referenceName={ctx.referenceName} />
    ),
  },
  restaurants: {
    buildLabel: (name) => (name ? `Restaurants Near ${name}` : 'Nearby Restaurants'),
    fetchData: fetchNearbyRestaurants,
    renderCard: (item, key, ctx) => (
      <NearbyRestaurantCard
        key={key}
        restaurant={item}
        referenceName={ctx.referenceName}
        // Threads the reference event's performance date (or the
        // page's own reservation date on the restaurant detail nearby
        // strip) onto the "Book now" URL so the restaurant booking
        // widget prefills with the SAME date. `ctx.dateFrom` is
        // already ISO `YYYY-MM-DD` (both sports/[id] and success
        // reformat `dateOfEvent` `DD/MM/YYYY → YYYY-MM-DD` before
        // passing it in).
        bookingDate={ctx.dateFrom}
      />
    ),
  },
  // Fifth category — concerts. Adapter hits
  // `GET /bip-api/concerts/nearby-concerts` (constructed on the fly
  // with `NEXT_PUBLIC_CONCERTS_API_URL`); card is a variant of the
  // sports card that routes to `/concerts/[id]` and suppresses the
  // tournament row. Consuming pages opt in via the standard
  // `excludeCategory` prop on `<Nearby>` — the sports detail /
  // success pages leave `'concerts'` OUT of their exclude list so
  // the strip renders alongside Hotels / Shows / Restaurants.
  concerts: {
    buildLabel: (name) => (name ? `Concerts Near ${name}` : 'Nearby Concerts'),
    fetchData: fetchNearbyConcerts,
    renderCard: (item, key, ctx) => <NearbyConcertCard key={key} concert={item} referenceName={ctx.referenceName} />,
  },
};
